import { JSX, useMemo } from 'react';
import orStatsListVariants from './or-stats-list.variants';
import classNames from 'classnames';

// components
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtHeader } from '@/components/atoms/at-header/at-header';

// types
import { EStatsListVariants, IOrStatsListProps } from './or-stats-list.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';

export const OrStatsList = ({
  variant = EStatsListVariants.BASIC,
  stats,
  useUppercaseInSubtitle = false,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrStatsListProps): JSX.Element => {
  const headerSize = useMemo(() => {
    if (variant === EStatsListVariants.BASIC) return 'h2';
    if (variant === EStatsListVariants.HOME_PLAN) return 'h3';
  }, [variant]);

  const textVariant = useMemo(() => {
    if (variant === EStatsListVariants.BASIC) return ETextVariants.STAT;
    if (variant === EStatsListVariants.HOME_PLAN) return ETextVariants.DEFAULT;
  }, [variant]);

  if (stats.length < 2) {
    return (
      <AtText>For OrStatsList, there is a minimum of 2 stats required</AtText>
    );
  }

  if (variant === EStatsListVariants.BASIC && stats.length > 4) {
    return (
      <AtText>
        For a Basic variant of OrStatsList, there is a maximum of 4 stats
        allowed
      </AtText>
    );
  }

  if (variant === EStatsListVariants.HOME_PLAN && stats.length > 5) {
    return (
      <AtText>
        For a HomePlan variant of OrStatsList, there is a maximum of 5 stats
        allowed
      </AtText>
    );
  }

  const { base, statContainer, title, subtitle } = orStatsListVariants({
    type: variant,
    transform: useUppercaseInSubtitle ? 'uppercase' : 'normal',
    itemsLength: stats.length.toString() as '2' | '3' | '4',
    topSpacing,
    bottomSpacing,
  });

  // todo: update theme to dynamic val
  return (
    <section data-theme="light" data-testid="or-stats-list">
      <div
        className={classNames('container mx-auto flex justify-center md:block')}
      >
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={base()}>
          {stats.map((stat, i) => (
            <div
              key={`stat-${stat.title}-${i}`}
              className={classNames(statContainer())}
              data-testid="or-stats-list-stat"
            >
              {variant === EStatsListVariants.BASIC ? (
                <>
                  <AtHeader
                    element="p"
                    variant={headerSize}
                    className={classNames(title())}
                  >
                    {stat.title}
                  </AtHeader>
                  <AtText
                    type="p"
                    className={classNames(subtitle())}
                    variant={textVariant}
                  >
                    {stat.subtitle}
                  </AtText>
                </>
              ) : (
                <>
                  <AtText
                    type="p"
                    aria-hidden={true}
                    className={classNames(subtitle())}
                    variant={textVariant}
                  >
                    {stat.subtitle}
                  </AtText>
                  <AtHeader
                    element="p"
                    variant={headerSize}
                    aria-hidden={true}
                    className={classNames(title())}
                  >
                    {stat.title}
                  </AtHeader>
                </>
              )}
            </div>
          ))}
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </div>
    </section>
  );
};
